import { http } from '@/api/http';

interface ApiEnvelope<T> {
  status: string;
  result: T;
  message?: string;
}

export interface ApiUserAddress {
  id?: number;
  user_id?: number;
  type?: string;
  phone?: string;
  mobile?: string;
  is_primary?: boolean;
}

export interface ApiUserAccount {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  about?: string;
  status?: number | string;
  image?: string;       // API uses "image", not "avatar"
  user_type_id?: number;
  role_id?: number;
  addresses?: ApiUserAddress[];
  // Resolved locally from addresses — not in the raw API response
  phone?: string;
  mobile?: string;
}

const USER_IMAGE_BASE = `${import.meta.env.VITE_ASSET_BASE_URL}/users/`;

const profileCache = new Map<string, ApiUserAccount>();

export async function getProfile(userId: number | string): Promise<ApiUserAccount> {
  const key = String(userId);
  if (profileCache.has(key)) return profileCache.get(key)!;
  const raw = await http.get<ApiEnvelope<ApiUserAccount & { data?: ApiUserAccount }>>(`/users/${userId}/account`);
  if (raw.status !== 'OK') throw new Error('Failed to load profile');
  const account = raw.result.data ?? raw.result;
  if (account.image && !account.image.startsWith('http')) {
    account.image = USER_IMAGE_BASE + account.image;
  }
  profileCache.set(key, account);
  return account;
}

export function invalidateProfileCache(userId?: number | string): void {
  if (userId != null) profileCache.delete(String(userId));
  else profileCache.clear();
}

export async function updatePersonalInfo(
  userId: number | string,
  payload: Partial<ApiUserAccount>,
): Promise<void> {
  const raw = await http.post<ApiEnvelope<unknown>>(`/users/${userId}/account`, payload);
  if (raw.status !== 'OK') {
    const r = raw.result as Record<string, unknown> | null;
    throw new Error((r?.error as string) ?? raw.message ?? 'Update failed');
  }
}

export async function uploadAvatar(userId: number | string, file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);
  const raw = await http.upload<ApiEnvelope<{ data?: string; url?: string }>>(`/users/avatar/${userId}`, form);
  if (raw.status !== 'OK') throw new Error('Failed to upload avatar');
  return raw.result?.data ?? raw.result?.url ?? '';
}

export async function changePassword(userId: number | string, password: string): Promise<void> {
  const raw = await http.post<ApiEnvelope<unknown>>(`/users/change-password/${userId}`, { password });
  if (raw.status !== 'OK') {
    const r = raw.result as Record<string, unknown> | null;
    throw new Error((r?.error as string) ?? raw.message ?? 'Failed to change password');
  }
}

export async function sendPasswordEmail(email: string): Promise<void> {
  await http.post<ApiEnvelope<unknown>>('/users/forgot-password', { email });
}
